import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useSettingsStore } from '@/store/settingsStore';
import { useDashboardFiltersStore } from '@/store/dashboardFiltersStore';
import {
  fetchDashboardData,
  type DashboardApiResponse,
  type DashboardQueryParams,
  type TimeSeriesData,
  type GeographicDataList,
  type PendingStepsData,
  type DeviceData,
  type BrowserData,
  type DurationPercentilesData
} from '../api/dashboard.api';
import type {
  RejectionReasonEntry,
  SdkVersionEntry,
  IdTypeEntry,
} from '../api/dashboard.schemas';

/**
 * Shape consumed by `RejectionReasonsChart`. Each entry mirrors the
 * server's `RejectionReasonEntry` plus a flag for whether the period
 * had any rejections at all (drives the empty-state).
 */
export interface RejectionReasonsData {
  data: RejectionReasonEntry[];
  totalRejections: number;
}

/**
 * Consolidated dashboard data type that includes all chart data
 */
export interface ConsolidatedDashboardData {
  // Raw API response
  raw: DashboardApiResponse;
  
  // Processed metrics
  metrics: {
    total_validations: number;
    successful_validations: number;
    failed_validations: number;
    pending_validations: number;
    validation_rate: number;
    unique_devices: number;
    approved_devices: number;
    device_conversion_rate: number;
    period: {
      start: string;
      end: string;
    };
  };
  
  // Chart data extracted from raw response
  charts: {
    validationsByDate: TimeSeriesData;
    geographicData: GeographicDataList;
    pendingSteps: PendingStepsData;
    deviceData: DeviceData;
    browserData: BrowserData;
    durationData: DurationPercentilesData;
    rejectionReasons: RejectionReasonsData;
    // (platform, version, count) rows ready for the SdkVersionChart.
    // Empty array when the backend hasn't emitted the field yet (older
    // metrics service builds).
    sdkVersions: SdkVersionEntry[];
    // (idType, count) rows for the Document Type widget. Empty array
    // when the backend hasn't emitted the field yet.
    idTypes: IdTypeEntry[];
  };
}

/**
 * Hook for fetching ALL dashboard data in a single query
 * This replaces multiple separate queries with one consolidated call
 */
export function useDashboardData(
  params?: DashboardQueryParams
): UseQueryResult<ConsolidatedDashboardData, Error> {
  const { selectedOrganization, selectedProject } = useSettingsStore();
  // Read cross-filters from the store and merge into the params the
  // API client sends. Keeping the merge here (not in `DashboardPage`)
  // means filter state ↔ data fetch are coupled in ONE place.
  const filters = useDashboardFiltersStore((s) => s.filters);

  const mergedParams: DashboardQueryParams | undefined =
    params || Object.keys(filters).length > 0
      ? { ...(params ?? { period: 'month' as const }), filters }
      : undefined;

  return useQuery({
    queryKey: [
      'dashboard',
      'consolidated',
      selectedOrganization?.id,
      selectedProject?.id,
      mergedParams,
    ],
    queryFn: async () => {
      // Single API call to get all dashboard data
      const rawData = await fetchDashboardData(mergedParams);
      
      // Process metrics from raw data
      const totalValidations = rawData.statusCounter.total || 0;
      const successfulValidations = rawData.statusCounter.finished.approved || 0;
      const failedValidations = rawData.statusCounter.finished.rejected || 0;
      const pendingValidations = Object.values(rawData.statusCounter.pendings).reduce((sum, count) => sum + count, 0);
      const validationRate = totalValidations > 0 ? (successfulValidations / totalValidations) * 100 : 0;
      const uniqueDevices = rawData.statusCounter.devices?.unique ?? 0;
      const approvedDevices = rawData.statusCounter.devices?.approved ?? 0;
      const deviceConversionRate = rawData.statusCounter.devices?.conversionRate ?? 0;

      // Create period based on params or default to current month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const metrics = {
        total_validations: totalValidations,
        successful_validations: successfulValidations,
        failed_validations: failedValidations,
        pending_validations: pendingValidations,
        validation_rate: Math.round(validationRate * 100) / 100,
        unique_devices: uniqueDevices,
        approved_devices: approvedDevices,
        device_conversion_rate: deviceConversionRate,
        period: {
          start: params?.startDate || startOfMonth.toISOString(),
          end: params?.endDate || endOfMonth.toISOString(),
        },
      };
      
      // Process chart data from raw response
      const charts = {
        // Validations by date chart
        validationsByDate: {
          labels: rawData.validationsByDate?.date || [],
          data: rawData.validationsByDate?.totalData?.map(value => value ?? 0) || [],
          title: 'Validations by Date',
          timeseries: true,
        } as TimeSeriesData,
        
        // Geographic data
        geographicData: (rawData.geolocations || [])
          .map((item: any, index: number) => {
            if (!item || typeof item !== 'object') return null;
            
            let coordinates = { lat: 0, lng: 0 };
            if (item.coordinates) {
              coordinates = {
                lat: Number(item.coordinates.lat) || 0,
                lng: Number(item.coordinates.lng) || 0,
              };
            } else if (item.lat !== undefined && item.lng !== undefined) {
              coordinates = {
                lat: Number(item.lat) || 0,
                lng: Number(item.lng) || 0,
              };
            }
            
            // Validate coordinates
            if (coordinates.lat < -90 || coordinates.lat > 90 || 
                coordinates.lng < -180 || coordinates.lng > 180) {
              return null;
            }
            
            return {
              id: item.id || `geo-${index}`,
              coordinates,
              country: item.country || item.country_name || null,
              country_code: item.country_code || item.countryCode || null,
              validations: Number(item.validations) || Number(item.count) || 0,
            };
          })
          .filter(Boolean) as GeographicDataList,
        
        // Pending steps chart
        pendingSteps: {
          data: Object.entries(rawData.statusCounter.pendings).map(([key, value]) => ({
            name: key.charAt(0).toUpperCase() + key.slice(1),
            value,
          })),
          title: 'Verification Steps Breakdown',
          type: 'bar',
        } as PendingStepsData,
        
        // Device data chart
        deviceData: {
          data: rawData.devicesCounter
            .sort((a: any, b: any) => b.count - a.count)
            .slice(0, 6)
            .map((device: any) => ({
              name: device.deviceName.length > 20
                ? device.deviceName.substring(0, 20) + '...'
                : device.deviceName,
              // Keep the full string so the cross-filter toggle can
              // send the canonical value back to the server.
              rawName: device.deviceName,
              value: device.count,
            })),
          title: 'Top Devices Used',
          type: 'pie',
        } as DeviceData,
        
        // Browser data chart
        browserData: {
          data: rawData.browsersCounter.map((browser: any) => ({
            name: browser.browserName,
            value: browser.count,
          })),
          title: 'Browsers Used',
          type: 'pie',
        } as BrowserData,
        
        // Duration data chart
        durationData: {
          data: rawData.validationDuration.map(item => ({
            name: item.name.toUpperCase(),
            value: parseFloat(item.value),
          })),
          title: 'Validation Duration Percentiles',
          type: 'bar',
          unit: 'seconds',
        } as DurationPercentilesData,

        // Rejection reasons donut — the server returns 5 fixed buckets
        // (see RejectionCategorySchema). Stripped of zero-count entries
        // so the donut doesn't render empty slices, but totalRejections
        // is kept for the center label and empty-state handling.
        rejectionReasons: {
          data: (rawData.rejectionReasons ?? []).filter(r => r.count > 0),
          totalRejections: failedValidations,
        },

        // SDK versions — server already returns top-N rows sorted desc.
        // Default to [] when the field is absent so the chart can render
        // its empty-state cleanly.
        sdkVersions: rawData.sdkVersionCounter ?? [],

        // Document types — same shape contract as sdkVersions.
        idTypes: rawData.idTypeCounter ?? [],
      };
      
      return {
        raw: rawData,
        metrics,
        charts,
      };
    },
    enabled: !!selectedOrganization && !!selectedProject,
    staleTime: 0, // Always consider data stale for immediate refresh
    gcTime: 10 * 60 * 1000, // 10 minutes cache time
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });
}

/**
 * Hook for getting dashboard status from consolidated data
 */
export function useDashboardStatus(params?: DashboardQueryParams) {
  const { data, isLoading, isError, isSuccess, error, refetch } = useDashboardData(params);
  
  const isEmpty = isSuccess && !!data && data.metrics.total_validations === 0;
  const hasData = isSuccess && !!data && data.metrics.total_validations > 0;
  
  return {
    isLoading,
    isError,
    isEmpty,
    isSuccess,
    hasData,
    error: error || null,
    retry: () => refetch(),
    data: data?.metrics,
  };
}